/**
 * VB Server - Channels API
 *
 * CRUD operations for organization channels.
 *
 * @example
 * import { listChannels, createChannel, updateChannel, deleteChannel } from '@/api/server/channels';
 *
 * const channels = await listChannels(orgId);
 * const newChannel = await createChannel(orgId, { name: 'Dispatch' });
 */

import { serverGet, serverPost, serverPut, serverDelete } from './client';

// =============================================================================
// Types
// =============================================================================

/**
 * Channel entity from vb-server
 */
export interface Channel {
  channel_id: string;
  org_id: string;
  name: string;
  notes?: string | null;
  frequency?: string | null;
  stream_url?: string | null;
  address?: string | null;
  group_id?: string | null;
  source_type?:
    | 'stream'
    | 'calls_groups'
    | 'calls_system'
    | 'calls_playlist'
    | 'tail'
    | 'ringcentral'
    | null;
  source_ref?: string | null;
  /** RingCentral only: extension numbers routed to this channel. Empty/NULL = catch-all. */
  extensions?: string[] | null;
  /** Non-null only when source_type='tail'. Firebase push id of the legacy org. */
  legacy_org_id?: string | null;
  /** Non-null only when source_type='tail'. Firebase push id of the legacy channel. */
  legacy_channel_id?: string | null;
  asr_model_id?: string | null;
  asr_context_id?: string | null;
  status: number;
  created: string;
  modified: string;
}

/**
 * Input for creating a new channel
 */
export interface CreateChannelInput {
  name: string;
  notes?: string;
  frequency?: string;
  stream_url?: string | null;
  source_type?:
    | 'stream'
    | 'calls_groups'
    | 'calls_system'
    | 'calls_playlist'
    | 'ringcentral'
    | null;
  source_ref?: string | null;
  extensions?: string[] | null;
  legacy_org_id?: string | null;
  legacy_channel_id?: string | null;
  asr_model_id?: string | null;
  asr_context_id?: string | null;
}

/**
 * Input for updating a channel
 */
export interface UpdateChannelInput {
  name?: string;
  notes?: string;
  frequency?: string;
  stream_url?: string | null;
  group_id?: string | null;
  source_type?:
    | 'stream'
    | 'calls_groups'
    | 'calls_system'
    | 'calls_playlist'
    | 'ringcentral'
    | null;
  source_ref?: string | null;
  extensions?: string[] | null;
  legacy_org_id?: string | null;
  legacy_channel_id?: string | null;
  asr_model_id?: string | null;
  asr_context_id?: string | null;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all channels for an organization
 *
 * @param orgId - Organization ID
 * @param options - Query options
 * @returns Array of channels
 */
export async function listChannels(
  orgId: string,
  options: { includeInactive?: boolean } = {}
): Promise<Channel[]> {
  const params = options.includeInactive ? '?include_inactive=true' : '';
  return serverGet<Channel[]>(`/orgs/${orgId}/channels${params}`);
}

/**
 * Get a single channel by ID
 *
 * @param orgId - Organization ID
 * @param channelId - Channel ID
 * @returns Channel or null if not found
 */
export async function getChannel(orgId: string, channelId: string): Promise<Channel | null> {
  try {
    const channels = await listChannels(orgId);
    return channels.find((c) => c.channel_id === channelId) || null;
  } catch {
    return null;
  }
}

/**
 * Create a new channel
 *
 * @param orgId - Organization ID
 * @param data - Channel data
 * @returns Created channel
 */
export async function createChannel(orgId: string, data: CreateChannelInput): Promise<Channel> {
  return serverPost<Channel>(`/orgs/${orgId}/channels`, data);
}

/**
 * Update an existing channel
 *
 * @param orgId - Organization ID
 * @param channelId - Channel ID
 * @param data - Updated channel data
 * @returns Updated channel
 */
export async function updateChannel(
  orgId: string,
  channelId: string,
  data: UpdateChannelInput
): Promise<Channel> {
  return serverPut<Channel>(`/orgs/${orgId}/channels/${channelId}`, data);
}

/**
 * Delete a channel (soft delete)
 *
 * @param orgId - Organization ID
 * @param channelId - Channel ID
 */
export async function deleteChannel(orgId: string, channelId: string): Promise<void> {
  await serverDelete(`/orgs/${orgId}/channels/${channelId}`);
}

/**
 * Restore a deleted channel
 *
 * @param orgId - Organization ID
 * @param channelId - Channel ID
 */
export async function restoreChannel(orgId: string, channelId: string): Promise<Channel> {
  return serverPost<Channel>(`/orgs/${orgId}/channels/${channelId}/restore`, {});
}
